import React, { useState } from "react";
import { Maximize2, Minimize2, Share2, type LucideIcon } from "lucide-react";
import { Button } from "./button";
import { ShareDialog } from "./share-dialog";
import { useTabs } from "@/contexts/tabs-context";
import { useSplitPanel } from "@/contexts/split-panel-context";
import { useCurrentTab } from "@/contexts/current-tab-context";
import { cn } from "@/lib/utils";

export interface ViewTitleProps {
	icon: LucideIcon;
	title: string;
	description?: string;
	channelId?: string;
	workspaceId?: string;
	actions?: React.ReactNode;
	className?: string;
	hideShareButton?: boolean;
	hideFocusButton?: boolean;
	onShare?: () => void;
}

export const ViewTitle: React.FC<ViewTitleProps> = ({
	icon: Icon,
	title,
	description,
	channelId,
	workspaceId,
	actions,
	className,
	hideShareButton = false,
	hideFocusButton = false,
	onShare,
}) => {
	const {
		activeTabId,
		focusedTabId,
		setFocusedTab,
		clearFocusedTab,
		tabs,
		setActiveTab,
	} = useTabs();
	const { focusPanel } = useSplitPanel();
	const { tab: currentTab } = useCurrentTab();
	const [shareDialogOpen, setShareDialogOpen] = useState(false);

	// Use the current tab from context if available, otherwise try to find it
	const thisTab =
		currentTab ||
		tabs.find(
			(tab) => tab.channelId === channelId && tab.workspaceId === workspaceId,
		);

	// Check if THIS specific tab is focused
	const isFocused = thisTab && focusedTabId === thisTab.id;

	const handleFocusToggle = () => {
		if (thisTab) {
			if (isFocused) {
				// If this tab is already focused, clear focus
				clearFocusedTab();
			} else {
				// First focus the panel that contains this tab
				if (thisTab.panelId) {
					focusPanel(thisTab.panelId);
				}
				// Set this tab as active first, then focus it
				setActiveTab(thisTab.id);
				setFocusedTab(thisTab.id);
			}
		}
	};

	const handleShare = () => {
		if (onShare) {
			onShare();
		} else {
			setShareDialogOpen(true);
		}
	};

	return <div></div>

	return (
		<>
			<div
				className={cn("border-border border-b bg-card px-6 py-2", className)}
			>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-3">
						<Icon className="h-5 w-5 text-muted-foreground" />
						<div>
							<h1 className="font-bold text-xl text-foreground">{title}</h1>
						</div>
					</div>
					<div className="flex items-center gap-2">
						{!hideShareButton && channelId && (
							<Button
								variant="ghost"
								size="icon"
								onClick={handleShare}
								title="Share this view"
								className="text-muted-foreground hover:text-foreground"
							>
								<Share2 className="h-4 w-4" />
							</Button>
						)}
						{!hideFocusButton && (
							<Button
								variant="ghost"
								size="icon"
								onClick={handleFocusToggle}
								title={isFocused ? "Exit focus mode" : "Enter focus mode"}
								className="text-muted-foreground hover:text-foreground"
							>
								{isFocused ? (
									<Minimize2 className="h-4 w-4" />
								) : (
									<Maximize2 className="h-4 w-4" />
								)}
							</Button>
						)}
						{actions}
					</div>
				</div>
			</div>
			{!hideShareButton && (
				<ShareDialog
					open={shareDialogOpen}
					onOpenChange={setShareDialogOpen}
					workspaceId={workspaceId}
					channelId={channelId}
					channelTitle={title}
				/>
			)}
		</>
	);
};
